import { EmployeeItem } from "@/components/employee/EmployeeItem.tsx";
import { FormResultForEmployeeModal } from "@/components/form-result/result-modal/FormResultForEmployeeModal.tsx";
import {
  ConfirmationModal,
  useModals,
  useToasts,
  parseUrlHelper,
  Box,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  LinkButtonTo,
  Stack,
  Text,
  ExpandableText,
} from "@skailer-com/uikit";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import {
  useCandidateFeedbackResendInvite,
  useScoreCardArchiveFeedback,
} from "@/queries/recruit/use-scorecards.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiFormElementType, ApiFormType, scoreMarkColors } from "@/types/forms/form.types.ts";
import { ApiCandidateFeedback, ApiScoreCardStatus } from "@/types/recruit/scorecard.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffectOnce } from "react-use";
import { twMerge } from "tailwind-merge";

export function ScoreCardFeedbackAction({
  feedback,
  dimmed,
  withEmployee,
  onDeleted,
}: {
  feedback: ApiCandidateFeedback;
  dimmed: boolean;
  withEmployee?: boolean;
  onDeleted?: (() => void) | undefined;
}) {
  const { t } = useTranslation();
  const { openModal, closeAll } = useModals();
  const { canIEdit } = usePermissions();
  const { renderRelativeDate } = useFormatter();
  const { mutateAsync: archiveFeedback } = useScoreCardArchiveFeedback();
  const { mutateAsync: resendInvite } = useCandidateFeedbackResendInvite();
  const { data: employee } = useEmployeeMe();

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const handleViewResults = () => {
    if (!feedback) return;

    const feedbackIdFromURL = parseUrlHelper.getNumber(searchParams, "feedback_id");

    openModal(FormResultForEmployeeModal, {
      employeeId: feedback.employee.id,
      questionnaireId: feedback.id,
      type: ApiFormType.scoreCard,
      onOpenModal: (feedbackId) => {
        if (!feedbackIdFromURL) {
          const newParams = new URLSearchParams(searchParams);
          newParams.append("feedback_id", `${feedbackId}`);
          navigate({ search: newParams.toString() }, { replace: true });
        }
      },
      onCloseModal: () => {
        const newParams = new URLSearchParams(searchParams);
        newParams.delete("feedback_id");
        navigate({ search: newParams.toString() }, { replace: true });
      },
    });

    analyticsService.trackEvent(analyticEvents.scoreCards.resultsViewed, {
      [analyticProperties.id]: feedback.id,
    });
  };

  useEffectOnce(() => {
    const feedbackId = parseUrlHelper.getNumber(searchParams, "feedback_id");

    if (feedbackId && Number(feedbackId) === feedback.id) {
      handleViewResults();
    }
  });

  const feedbackText = useMemo(() => {
    let title: string | null | undefined;
    let answer: string | null | undefined;

    switch (feedback?.question_element?.type) {
      case ApiFormElementType.scale: {
        title = feedback?.question_element?.scale_settings?.title;

        const optionIds = feedback?.question_answer?.answer_scale_option_ids;
        const scales = feedback?.question_element?.scale_settings?.form_scale.scale;
        const answers = scales?.filter((option) => optionIds?.includes(option.id));

        answer = answers?.map((a) => a.title).join(", ");

        break;
      }
      case ApiFormElementType.open_answer:
        title = feedback?.question_element?.open_answer_settings?.title;
        answer = feedback?.question_answer?.answer_text || feedback?.question_answer?.comment;

        break;
      case ApiFormElementType.employee_nps:
        title = feedback?.question_element?.employee_nps_settings?.title;
        answer = `${feedback?.question_answer?.answer_int}` || feedback?.question_answer?.comment;
        break;
      case ApiFormElementType.select: {
        title = feedback?.question_element?.select_settings?.title;

        const optionIds = feedback?.question_answer?.answer_select_option_ids;
        const options = feedback?.question_element?.select_settings?.options;
        const answers = options?.filter((option) => optionIds?.includes(option.id));

        answer = answers?.map((a) => a.title).join(", ");

        break;
      }
    }

    return answer
      ? `${title ? `${title}${title.at(-1) === "?" ? "" : ":"} ` : ""}${answer ? `${answer}...` : ""}`
      : "";
  }, [feedback]);

  const feedbackScoreColor = useMemo(() => {
    if (!feedback) return undefined;
    if (!feedback.avg_score_mark) return undefined;

    return feedback.avg_score_mark ? scoreMarkColors[feedback.avg_score_mark] : undefined;
  }, [feedback]);

  const handleArchiveFeedback = () => {
    if (!feedback) return;

    openModal(ConfirmationModal, {
      question: t("common:attention"),
      text: t("common:delete_confirm"),
      acceptTitle: t("common:delete"),
      onAccept: async function () {
        try {
          await archiveFeedback({
            candidateId: feedback.candidate_id,
            feedbackId: feedback.id,
          });

          analyticsService.trackEvent(analyticEvents.scoreCards.archiveFeedback, {
            [analyticProperties.id]: feedback.id,
          });

          if (onDeleted) onDeleted();
        } catch {
          //
        }
      },
    });
  };

  const { showInfo } = useToasts();
  const handleResendInvite = useCallback(async () => {
    if (!feedback) return;
    const success = await resendInvite({
      candidateId: feedback.candidate_id,
      feedbackId: feedback.id,
    });

    if (success) {
      showInfo({
        title: t("scorecards:invitation_resent"),
      });
    }
  }, [feedback]);

  const handleEditFeedback = () => {
    if (!feedback) return;
    closeAll();
    navigate(`/recruitment/candidates/${feedback.candidate_id}/feedbacks/${feedback.id}`);
  };

  return (
    <>
      {dimmed ? (
        <Text className={"text-ellipsis overflow-hidden"}>
          <b>{t("scorecards:feedback")}</b>: {feedback?.scorecard.title}
        </Text>
      ) : (
        <Stack
          className={twMerge(
            "rounded-xs bg-dark/5 border border-dark/5 p-4 group",
            feedback.status !== ApiScoreCardStatus.completed || feedback.is_archived
              ? "opacity-50"
              : ""
          )}
          gap={"smd"}
        >
          {withEmployee && (
            <Stack gap={"md"} items={"center"} justify={"between"} direction={"horizontal"}>
              <Stack direction={"horizontal"} gap={"sm"}>
                <EmployeeItem
                  employee={feedback.employee}
                  size={"sm"}
                  variant={"secondary"}
                  direction={"horizontal"}
                  showPosition={false}
                  caption={feedback.completed_at && renderRelativeDate(feedback.completed_at)}
                />
              </Stack>
              {!feedback.is_archived && canIEdit(ApiPermission.manage_candidates) && (
                <Box>
                  <DropdownMenu>
                    {feedback.status !== ApiScoreCardStatus.completed && (
                      <DropdownMenuItem
                        onClick={handleResendInvite}
                        leftIcon={<FontAwesomeIcon icon="fa-light fa-paper-plane" />}
                        title={t("scorecards:resend_invite")}
                      />
                    )}
                    {feedback.status == ApiScoreCardStatus.completed &&
                      feedback.employee.id == employee?.id && (
                        <DropdownMenuItem
                          onClick={handleEditFeedback}
                          leftIcon={<FontAwesomeIcon icon="fa-light fa-pen-to-square" />}
                          title={t("common:edit") + "..."}
                        />
                      )}
                    <DropdownMenuItem
                      onClick={handleArchiveFeedback}
                      variant={"menuDanger"}
                      leftIcon={<FontAwesomeIcon icon="fa-light fa-trash" />}
                      title={t("common:delete") + "..."}
                    />
                  </DropdownMenu>
                </Box>
              )}
            </Stack>
          )}
          <Stack gap={"sm"} className={twMerge(feedback.is_archived && "line-through")}>
            <Stack
              className={"whitespace-nowrap"}
              direction={"horizontal"}
              gap={"sm"}
              items={"center"}
            >
              {feedback.status === ApiScoreCardStatus.completed && feedbackScoreColor && (
                <Stack
                  className={twMerge(
                    "w-6 h-6 rounded-xs shrink-0 justify-center align-center text-light text-center text-sm",
                    feedbackScoreColor
                  )}
                >
                  {feedback?.avg_scale_score && Number(feedback?.avg_scale_score.toFixed(1))}
                </Stack>
              )}
              <Text className={"text-ellipsis overflow-hidden"}>
                <b>{t("scorecards:feedback")}</b>: {feedback.is_archived && t("common:deleted")}
                {!feedback.is_archived && feedback?.scorecard.title}
              </Text>
            </Stack>
            {feedback.status === ApiScoreCardStatus.completed &&
              feedbackText &&
              !feedback.is_archived && (
                <ExpandableText maxLines={1} showLess className="text-base whitespace-pre-wrap">
                  {feedbackText}
                </ExpandableText>
              )}
          </Stack>
          {feedback.status === ApiScoreCardStatus.completed && !feedback.is_archived ? (
            <LinkButtonTo
              onClick={handleViewResults}
              className={"text-brand-dark font-semibold text-sm no-underline"}
            >
              {t("scorecards:view_feedback")}
            </LinkButtonTo>
          ) : (
            ""
          )}
        </Stack>
      )}
    </>
  );
}
